import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Factory, Zap, Award, ChevronRight, Target, Battery, Home, CheckCircle } from 'lucide-react';
import { t } from '../utils/translation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import PageHeader from '../components/PageHeader';
import SectionSplitter from '../components/SectionSplitter';
import moldingImage from '../assets/manufacturing/molding-workshop.jpg';
import stampingImage from '../assets/manufacturing/stamping-services.jpg';
import cncImage from '../assets/manufacturing/mold-development.jpg';
import assemblyLinesImg from '../assets/manufacturing/assembly-lines.jpg';
import qcMfgImg from '../assets/manufacturing/quality-control-mfg.jpg';

const Manufacturing = ({ theme, language }) => {
  const capabilities = [
    { 
      title: t('injectionMolding', language), 
      desc: t('customPlastic', language), 
      icon: <Zap className="w-8 h-8 text-red-600" />,
      details: [
        t('multiCavity', language),
        t('materialPA', language),
        t('tolerance', language),
        t('productionCapacity', language)
      ],
      image: moldingImage
    },
    { 
      title: t('stampingWorkshop', language), 
      desc: t('precisionCopper', language), 
      icon: <Factory className="w-8 h-8 text-red-600" />,
      details: [
        t('progressiveDie', language),
        t('materialBrass', language),
        t('plating', language),
        t('thickness', language)
      ],
      image: stampingImage
    },
    { 
      title: t('moldDevelopment', language), 
      desc: t('inHouseMold', language), 
      icon: <Target className="w-8 h-8 text-red-600" />,
      details: [
        t('cadcad', language),
        t('moldFlow', language),
        t('rapidPrototyping', language),
        t('moldMaintenance', language)
      ],
      image: moldingImage
    },
    { 
      title: t('cncMachining', language), 
      desc: t('highPrecision', language), 
      icon: <Battery className="w-8 h-8 text-red-600" />,
      details: [
        t('edm', language),
        t('wireCutting', language),
        t('cncMilling', language),
        t('surfaceFinishing', language)
      ],
      image: cncImage
    },
    { 
      title: t('electroplating', language) || 'Electroplating & Plating',
      desc: t('metalFinishing', language) || 'Professional metal surface treatment including nickel, gold, tin, and silver plating for optimal conductivity and corrosion resistance.',
      icon: <Battery className="w-8 h-8 text-red-600" />,
      details: [
        t('nickelPlating', language) || 'Nickel Plating',
        t('goldPlating', language) || 'Gold Plating',
        t('tinPlating', language) || 'Tin Plating',
        t('roshCompliant', language) || 'RoHS Compliant'
      ],
      image: stampingImage
    },
    { 
      title: t('assemblyLines', language), 
      desc: t('automatedConnector', language), 
      icon: <Home className="w-8 h-8 text-red-600" />,
      details: [
        t('semiAutomatic', language),
        t('fullyAutomatic', language),
        t('inlineTesting', language),
        t('cleanRoom', language)
      ],
      image: assemblyLinesImg
    },
    { 
      title: t('qualityControl', language), 
      desc: t('rohsCertified', language), 
      icon: <CheckCircle className="w-8 h-8 text-red-600" />,
      details: [
        t('incomingMaterial', language),
        t('inProcess', language),
        t('finalProduct', language),
        t('reliabilityTesting', language)
      ],
      image: qcMfgImg
    }
  ];

  const certifications = [
    { name: 'RoHS', desc: t('restrictionHazardous', language) },
    { name: 'ISO 9001', desc: t('qualityManagement', language) }
  ];

  const process = [
    { step: '01', title: t('designReview', language), desc: t('analyzeTechnical', language) },
    { step: '02', title: t('prototyping', language), desc: t('createSamples', language) },
    { step: '03', title: t('tooling', language), desc: t('developMolds', language) },
    { step: '04', title: t('production', language), desc: t('massProduction', language) },
    { step: '05', title: t('testing', language), desc: t('comprehensiveInspection', language) },
    { step: '06', title: t('delivery', language), desc: t('packagingShipping', language) }
  ];

  // Capabilities section with scroll animation
  const [capabilitiesRef, capabilitiesVisible] = useScrollAnimation(0.1);
  
  // Process section with scroll animation
  const [processRef, processVisible] = useScrollAnimation(0.1);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      <Helmet>
        <title>{t('manufacturingCapabilities', language)} - Qingshun Electronics | {t('inHouseProduction', language)}</title>
        <meta name="description" content={t('manufacturingDescription', language)} />
        <meta name="keywords" content="manufacturing capabilities, injection molding, stamping, mold development, CNC machining, assembly lines, quality control, in-house production" />
        <meta name="author" content="Qingshun Electronics Technology Co., Ltd." />
        <link rel="canonical" href="https://qsconn.com/manufacturing" />
        <meta name="description" content={t('manufacturingDescription', language)} />
        <meta name="keywords" content="in-house manufacturing, injection molding, stamping services, mold development, CNC machining, cable assembly, connector production, electronics manufacturing China, Suzhou factory" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://qsconn.com/manufacturing" />
        <meta property="og:title" content={`${t('manufacturingCapabilities', language)} | Injection Molding, Stamping & CNC Machining - Qingshun Electronics`} />
        <meta property="og:description" content={t('manufacturingDescription', language)} />
        <meta property="og:image" content="https://qsconn.com/logo512.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://qsconn.com/manufacturing" />
        <meta property="twitter:title" content={`${t('manufacturingCapabilities', language)} | Qingshun Electronics - In-House Production in China`} />
        <meta property="twitter:description" content={t('manufacturingDescription', language)} />
        <meta property="twitter:image" content="https://qsconn.com/logo512.png" />
        <meta property="twitter:image:alt" content={`${t('manufacturingCapabilities', language)} - Qingshun Electronics`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["Organization", "Manufacturer"],
            "name": "Qingshun Electronics Technology Co., Ltd.",
            "description": t('completeInHouse', language),
            "url": "https://qsconn.com/",
            "knowsAbout": capabilities.map(cap => cap.title).join(", "),
            "hasCapability": capabilities.map(cap => ({
              "@type": "Thing",
              "name": cap.title,
              "description": cap.desc
            }))
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://qsconn.com/"},
              {"@type": "ListItem", "position": 2, "name": t('manufacturing', language), "item": "https://qsconn.com/manufacturing"}
            ]
          })}
        </script>
      </Helmet>

      <PageHeader
        theme={theme}
        badge={t('manufacturing', language)}
        title={t('manufacturingCapabilities', language)}
        subtitle={t('completeInHouse', language)}
      />

      {/* Capabilities Grid */}
      <section 
        ref={capabilitiesRef}
        className="py-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('ourCoreCapabilities', language)}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('stateOfTheArt', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-8 shadow-lg transform hover:-translate-y-2 transition-all duration-300 hover:bg-gradient-to-br ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600 from-red-900/10 to-red-900/5' : 'bg-white border-2 border-gray-300 hover:border-red-600 from-red-100 to-red-50'}`}
                style={{
                  animation: capabilitiesVisible ? `fadeInUp 0.6s ease-out ${idx * 0.1}s both` : 'none',
                }}
              >
                <div className="mb-4 rounded-xl overflow-hidden">
                  <div className={`relative rounded-xl overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'border-2 border-red-900/50' : 'border-2 border-red-500'}`}>
                    <img 
                      src={cap.image} 
                      alt={`${cap.title} ${t('facility', language)}`} 
                      className="w-full h-40 transition-transform duration-500 hover:scale-105 object-cover transform hover:scale-110 transition duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t transition-colors duration-300 ${theme === 'dark' ? 'from-black/70' : 'from-white/70'} to-transparent`}></div>
                  </div>
                </div>
                <div className="mb-4 text-2xl">{cap.icon}</div>
                <h3 className="text-2xl font-bold mb-3 text-red-600">{cap.title}</h3>
                <p className="mb-6">{cap.desc}</p>
                <ul className="space-y-2">
                  {cap.details.map((detail, detailIdx) => (
                    <li key={detailIdx} className="flex items-start">
                      <ChevronRight className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
                      <span className="text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="slant" />

      {/* Manufacturing Process */}
      <section 
        ref={processRef}
        className="py-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('manufacturingProcess', language)}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('fromConcept', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((item, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-8 shadow-lg relative overflow-hidden transform hover:-translate-y-1 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}
                style={{
                  animation: processVisible ? `fadeInUp 0.5s ease-out ${idx * 0.1}s both` : 'none',
                }}
              >
                <div className={`absolute top-4 right-4 text-6xl font-bold transition-colors duration-300 ${theme === 'dark' ? 'text-red-900/20' : 'text-red-200'}`}>{item.step}</div>
                <div className="relative z-10">
                  <div className="text-3xl font-bold text-red-600 mb-3 transition-colors duration-300">{item.step}</div>
                  <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-gray-900'}`}>{item.title}</h3>
                  <p className={`transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="wave" flip />

      {/* Certifications */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Award className={`w-16 h-16 mx-auto mb-5 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('industryCertifications', language)}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('compliance', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert, idx) => (
              <div key={idx} className={`rounded-2xl p-8 text-center shadow-lg transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}>
                <div className={`flex justify-center mb-4 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`}>
                  <CheckCircle className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-red-400 transition-colors duration-300">{cert.name}</h3>
                <p className={`transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="dots" />

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`rounded-3xl p-12 shadow-lg relative overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-red-900/50' : 'bg-white border-2 border-red-200'}`}>
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('readyToStartProject', language)}
            </h2>
            <p className={`text-xl mb-8 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('contactUsDiscuss', language)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-4 rounded-xl text-lg font-semibold text-white hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/30 transform hover:scale-105 duration-300"
              >
                {t('requestQuote', language)}
              </a>
              <a 
                href="/services" 
                className={`px-8 py-4 rounded-xl text-lg font-semibold border-2 transition-colors duration-300 ${theme === 'dark' ? 'border-red-600 text-red-400 hover:bg-red-900/30' : 'border-red-600 text-red-600 hover:bg-red-100'}`}
              >
                {t('viewAllServices', language)}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Manufacturing;